/**
 * Checkout discounts — the preview the barber sees in the booking panel.
 *
 * Mirrors planDiscount() in apps/backend/src/discounts.ts (same cents
 * rounding). The backend recomputes and is what charges the terminal, so
 * keep the two in step: a drift here shows a wrong preview, never a wrong
 * charge.
 */
export type DiscountType = 'loyalty' | 'voucher';

export interface DiscountChoice {
  type: DiscountType;
  /** loyalty — the booking_service row that is free. */
  bookingServiceId?: number;
}

export const DISCOUNT_LABELS: Record<DiscountType, string> = {
  loyalty: 'Loyalty card',
  voucher: 'Voucher Discount',
};

const VOUCHER_DISCOUNT_RATE = 0.1;
const toCents = (dollars: number | string) => Math.round((Number(dollars) || 0) * 100);

export interface DiscountPreview {
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  /** loyalty — the line that is free (defaults to the first service). */
  freeBookingServiceId: number | null;
}

export function previewDiscount(
  services: { booking_service_id?: number | string; price: number | string }[],
  products: { price: number; qty: number }[],
  choice: DiscountChoice | null,
): DiscountPreview {
  const serviceCents = services.map((s) => ({
    id: Number(s.booking_service_id),
    cents: toCents(s.price),
  }));
  const productCents = products.map((p) => toCents(p.price) * Math.max(1, Number(p.qty) || 1));
  const subtotalCents =
    serviceCents.reduce((sum, s) => sum + s.cents, 0) + productCents.reduce((sum, c) => sum + c, 0);

  let discountCents = 0;
  let freeBookingServiceId: number | null = null;
  if (choice?.type === 'loyalty' && serviceCents.length > 0) {
    const free = serviceCents.find((s) => s.id === choice.bookingServiceId) ?? serviceCents[0];
    freeBookingServiceId = free.id;
    discountCents = free.cents;
  } else if (choice?.type === 'voucher') {
    discountCents =
      serviceCents.reduce((sum, s) => sum + Math.round(s.cents * VOUCHER_DISCOUNT_RATE), 0) +
      productCents.reduce((sum, c) => sum + Math.round(c * VOUCHER_DISCOUNT_RATE), 0);
  }

  return {
    subtotalCents,
    discountCents,
    totalCents: subtotalCents - discountCents,
    freeBookingServiceId,
  };
}
